import { useEffect, useRef } from 'react';

/**
 * PRD-082: Scrolls focused input elements into view on mobile
 * when the virtual keyboard opens and obscures the field.
 * Only activates on touch devices (pointer: coarse).
 * Debounced to prevent jittery scrolling on rapid focus changes.
 * Skips inputs inside modals (they have their own scroll context).
 */
export default function useScrollToFocused() {
  const timeoutRef = useRef(null);

  useEffect(() => {
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    if (!isTouch) return;

    const handleFocusIn = (e) => {
      const tag = e.target.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;

      // Skip inputs inside modals — modals manage their own scroll
      if (e.target.closest('.modal-overlay, .modal-content, .dm-window, .session-detail-modal')) return;

      // Cancel any pending scroll from a previous focus event
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      const target = e.target;
      timeoutRef.current = setTimeout(() => {
        if (target.isConnected) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        timeoutRef.current = null;
      }, 350);
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
}
